<template>
  <zm-virtual-table
    ref="virtualTable"
    class="v3-comparison-table"
    :data="displayData"
    row-key="id"
    :height="maxHeight"
    :keeps="10"
    :estimate-size="100"
    stripe
    :style="`width: ${width}`"
    :row-class-name="getRowClassName"
    @row-click="handleRowClick"
    show-expand-icon="false"
  >
    <!-- Feature Column -->
    <zm-virtual-table-column
      prop="feature"
      label=""
      class-name="v3-comparison-table__feature-col"
    >
      <template #default="{ row }">
        <div
          :class="row.subTitle ? `${row.id}-sub-title` : ''"
          style="position: relative;"
        >
          <span
            :aria-labelby="`${row.id}_feature`"
            :class="[
              row.subTitle ? 'sub-title' : 'sub-text',
              row.footerDesc ? 'sub-title__footer' : ''
            ]"
          >
            <template v-if="Array.isArray(row.feature)">
              <div v-for="(item, index) in row.feature" :key="index">
                <span
                  tabindex="-1"
                  :id="`${row.id}_${index}_feature`"
                  v-html="item"
                  style="margin-right: 4px;padding-right: 10px;"
                />
              </div>
            </template>
            <span
              v-else
              tabindex="-1"
              :id="`${row.id}_feature`"
              v-html="row.feature"
              style="margin-right: 4px;padding-right: 10px;"
            />
            <zm-icon
              v-if="row.subTitle && row.hasChildren"
              class="v3-comparison-table__expand-arrow"
              size="20px"
            >
              <ManualChevronDownSmall v-if="expandedRows.has(row.id)" />
              <ManualChevronRightSmall v-else />
            </zm-icon>
            <zm-tooltip
              v-if="row.tooltip"
              placement="top"
            >
              <template #content>
                <div v-html="row.tooltip" />
              </template>
              <zm-icon
                class="v3-comparison-table__tooltip-icon"
                color="#00031F"
                size="14px"
                style="cursor: pointer; display: inline-block; vertical-align: middle;"
                @click.stop=""
              >
                <Information />
              </zm-icon>
            </zm-tooltip>
          </span>

          <AutoExpireBadge
            v-if="row.badgeId"
            :badgeId="row.badgeId"
            theme="comparison"
            style="margin-left: 4px;"
          />
          <a
            v-if="row.pdf"
            data-testid="checkout-a-23"
            style="font-size: 12px;"
            tabindex="-1"
            href="https://zoom.us/docs/doc/Phone-NomadicE811-6-2020.pdf"
            target="_blank"
            @click.stop
          >
            {{$t('phone.fullPlanItem20Pdf')}}
          </a>
          <div v-if="row.link">
            <a
              v-if="row.link.callback"
              data-testid="checkout-a-24"
              tabindex="-1"
              href="javascript:void(0);"
              @click.stop="row.link.callback"
              :style="row.link.style || {}"
              :aria-label="row.link.ariaLabel || row.link.text"
            >
              {{ row.link.text }}
            </a>
          </div>
        </div>
      </template>
    </zm-virtual-table-column>

    <!-- Dynamic Columns -->
    <zm-virtual-table-column
      v-for="(col, idx) in colConfig"
      :key="col.key + idx"
      :prop="col.key"
      :label="col.title"
      width="210"
      class-name="v3-comparison-table__col"
    >
      <template #header="{ column }">
        <div class="header-content">
          <div tabindex="-1" class="type-title">{{ column.label }}</div>
          <template v-if="!col.hideBtn">
            <zm-icon
              v-if="col.category === value || col.selected"
              tabindex="-1"
              class="no-event-btn"
              :aria-label="col.title + ' Selected'"
            >
              <CheckmarkFill />
            </zm-icon>
            <zm-tooltip v-else-if="col.btn && col.btn.disabledTips" :disabled="!col.btn.disabled">
              <template #content>
              <p @click="onSubmitFormClick(col, $event)" v-html="col.btn.disabledTips"/>
              </template>
              <div>
                <zm-button
                  :data-testid="`checkout-button-comparison-table-select-${col.category}`"
                  tabindex="-1"
                  :type="col.btn.type"
                  @click="col.btn.callback(col)"
                  :disabled="col.btn.disabled"
                  :aria-label="col.btn.ariaLabel"
                  class="comparison-select-btn"
                >
                  {{ col.btn.label }}
                </zm-button>
              </div>
            </zm-tooltip>
            <zm-button
              v-else-if="col.btn"
              :data-testid="`checkout-button-comparison-table-select-${col.category}`"
              tabindex="-1"
              :type="col.btn.type"
              @click="col.btn.callback(col)"
              :disabled="col.btn.disabled"
              :aria-label="col.btn.ariaLabel"
              class="comparison-select-btn"
            >
              {{ col.btn.label }}
            </zm-button>
            <div v-else class="empty-btn" aria-hidden="true" />
          </template>
        </div>
      </template>

      <template #default="{ row }">
        <div v-if="!row.footerDesc">
          <template v-if="col.key !== 'enterprise'">
            <template v-if="row.isString">
              <span tabindex="-1" v-html="row[col.key]" v-if="row[col.key]" />
              <i tabindex="-1" :aria-label="$t('notIncluded')" v-else></i>
            </template>
            <template v-else>
              <zm-icon
                v-if="row[col.key] === true"
                tabindex="-1"
                class="primary-icon"
                :aria-label="$t('included')"
              >
                <CheckmarkFill />
              </zm-icon>
              <span
                v-else-if="row[col.key] && row.mixed && typeof row[col.key] === 'string'"
                tabindex="-1"
                v-html="row[col.key]"
              />
              <i
                v-else
                tabindex="-1"
                :aria-label="$t('notIncluded')"
              ></i>
              <template v-if="row.desc">
                <div
                  v-if="typeof row.desc === 'string'"
                  tabindex="-1"
                  class="cell-desc"
                  v-html="row.desc"
                />
                <div
                  v-if="typeof row.desc === 'object'"
                  tabindex="-1"
                  class="cell-desc"
                  v-html="row.desc[col.key]"
                />
              </template>
            </template>
          </template>
          <template v-else>
            <template v-if="row.isString">
              <span tabindex="-1" v-html="row.enterprise" v-if="row.enterprise" />
              <i tabindex="-1" :aria-label="$t('notIncluded')" v-else></i>
              <div v-if="row.enterpriseDesc" tabindex="-1" class="cell-desc">
                *{{ row.enterpriseDesc }}
              </div>
            </template>
            <template v-else>
              <template v-if="row.enterprise">
                <zm-icon
                  v-if="row.enterprise === true"
                  tabindex="-1"
                  class="primary-icon"
                  :aria-label="$t('included')"
                >
                  <CheckmarkFill />
                </zm-icon>
                <div v-else tabindex="-1">{{ row.enterprise }}</div>
                <div
                  v-if="row.enterpriseDesc"
                  tabindex="-1"
                  class="cell-desc"
                  v-html="row.enterpriseDesc"
                />
                <template v-if="row.desc">
                  <div
                    v-if="typeof row.desc === 'string'"
                    tabindex="-1"
                    class="cell-desc"
                    v-html="row.desc"
                  />
                  <div
                    v-if="typeof row.desc === 'object'"
                    tabindex="-1"
                    class="cell-desc"
                    v-html="row.desc[col.key]"
                  />
                </template>
              </template>
              <i v-else tabindex="-1" :aria-label="$t('notIncluded')"></i>
            </template>
          </template>
        </div>
      </template>
    </zm-virtual-table-column>
  </zm-virtual-table>
</template>

<script>
import { A_LINK_TAG_NAME } from '@/common/constants';
import { ZmVirtualTable, ZmVirtualTableColumn, ZmTooltip, ZmIcon } from '@zoom/zoom-ui-vue3';
import { ManualChevronRightSmall, ManualChevronDownSmall, InformationSmall as Information, CheckmarkFill } from '@zoom/icons-vue';

export default {
  name: 'FullComparisonTable',
  components: {
    ZmVirtualTable,
    ZmVirtualTableColumn,
    ZmTooltip,
    ZmIcon,
    ManualChevronRightSmall,
    ManualChevronDownSmall,
    Information,
    CheckmarkFill
  },
  data() {
    return {
      errorHandler: null,
      rejectionHandler: null,
      expandedRows: new Set(['meeting', 'phone', 'phoneAdmin', 'features'])
    };
  },
  props: {
    treeData: {
      type: Array,
      required: true
    },
    colConfig: {
      type: Array,
      required: true
    },
    value: {
      type: String,
      default: ''
    },
    width: {
      type: String,
      default: '944px'
    },
    maxHeight: {
      type: Number,
      default: 500
    }
  },
  computed: {
    displayData() {
      const result = [];

      this.treeData.forEach(item => {
        result.push({
          ...item,
          hasChildren: item.children && item.children.length > 0
        });

        if (this.expandedRows.has(item.id) && item.children) {
          item.children.forEach(child => {
            result.push({
              ...child,
              isChild: true
            });
          });
        }
      });

      return result;
    }
  },
  methods: {
    handleRowClick(row) {
      if (row.hasChildren || (row.children && row.children.length > 0)) {
        if (this.expandedRows.has(row.id)) {
          this.expandedRows.delete(row.id);
        } else {
          this.expandedRows.add(row.id);
        }
        this.$forceUpdate();
      }
    },
    getRowClassName(row) {
      if (row.subTitle) {
        return 'v3-comparison-table__section-row';
      }
      if (row.isChild) {
        return 'v3-comparison-table__feature-row';
      }
      return '';
    },
    onSubmitFormClick(col, event) {
      try {
        if (event?.target?.tagName === A_LINK_TAG_NAME) {
          this.$tracking.buyInteraction(
            'product details',
            `full plan comparison select - ${col?.category || ''} - contact form`
          );
        }
      } catch (error) {
        console.warn('Error in onSubmitFormClick:', error);
      }
    },
  }
};
</script>

<style lang="scss">



.v3-comparison-table {

  .v3-comparison-table__feature-col {
    text-align: left;
    font-style: normal;
    font-weight: 450;
    font-size: 16px;
    line-height: 150%;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    padding: 16px 0;

    &:first-child {
      border-top-left-radius: 8px;
    }

    .sub-text {
      font-style: normal;
      font-weight: 450;
      font-size: 14px;
      line-height: 18px;
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    }
  }

  .v3-comparison-table__col {
    text-align: center;
    font-style: normal;
    font-weight: 450;
    font-size: 14px;
    line-height: 150%;
    color: var(--zoom-color-text-strong-neutral, #00031F);
    padding: 20px 4px;

    .cell {
      flex-direction: column !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 8px !important;

      .type-title {
        margin-bottom: 8px !important;
      }
    }

    &:last-child {
      border-bottom-right-radius: 8px;
      border-top-right-radius: 8px;
    }

    .cell-desc {
      font-style: normal;
      font-weight: 500;
      font-size: 12px;
      line-height: 150%;
      text-align: center;
      color: #666487;
      display: block;
      margin-top: 4px;
    }

    a {
      color: var(--zoom-color-fill-primary, #0d6bde);
      text-decoration: none;
    }
  }

  .v3-comparison-table__feature-col .sub-title {
    margin-left: 0;
    text-transform: capitalize;
    font-style: normal;
    font-weight: 600;
    font-size: 20px;
    line-height: 150%;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    padding: 20px 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: flex-start;

    &.sub-title__footer {
      color: #666487;
      font-size: 12px;
      line-height: 18px;
      font-weight: normal;
    }
  }


  .header-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;

    .type-title {
      margin-bottom: 8px;
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    }

    .no-event-btn {
      color: var(--zoom-color-fill-primary, #0d6bde);
      height: 32px;
      line-height: 32px;
      font-size: 20px;

      svg {
        width: 20px;
        height: 20px;
      }
    }
  }

  a {
    color: #0e71eb;
    font-weight: 400;
    text-decoration: none;
  }

  &__icon-btn {
    background-color: transparent;
    border: none;
    font-size: 14px;
    line-height: 14px;
    cursor: pointer;
  }

  &__expand-arrow {
    display: inline-block;
    margin-left: 0px;
    vertical-align: middle;
    font-size: 20px;
    line-height: 1;
    width: 20px;
    height: 20px;
    svg {
      color: var(--zoom-color-text-strong-neutral, #00031F) !important;
      fill:var(--zoom-color-text-strong-neutral,#00031F) !important;
     }
    }

  &__tooltip-icon {
    svg {
      width: 14px;
      height: 14px;
      color: var(--zoom-color-text-strong-neutral, #00031F) !important;
      fill:var(--zoom-color-text-strong-neutral,#00031F) !important;
    }
  }

  .primary-icon {
    color: var(--zoom-color-fill-primary, #0d6bde);
    font-size: 20px;

    svg {
      width: 20px;
      height: 20px;
    }
  }

}

// Comparison table select buttons
.comparison-select-btn {
  width: 100%;
  box-sizing: border-box;
  font-weight: normal !important;
  display: block;
}

// Ensure button container takes full width
.v3-comparison-table__col .header-content {
  width: 100%;

  > div {
    width: 100%;
  }
}

.empty-btn {
  height: 32px;
}
</style>
